import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface CafeOwner {
  id: string;
  email: string;
  name: string;
  phone: string;
  cafeId: string;
  cafeName: string;
}

interface CafeOwnerAuthContextType {
  isAuthenticated: boolean;
  cafeOwner: CafeOwner | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const CafeOwnerAuthContext = createContext<CafeOwnerAuthContextType | undefined>(undefined);

export const useCafeOwnerAuth = () => {
  const context = useContext(CafeOwnerAuthContext);
  if (context === undefined) {
    throw new Error('useCafeOwnerAuth must be used within a CafeOwnerAuthProvider');
  }
  return context;
};

interface CafeOwnerAuthProviderProps {
  children: ReactNode;
}

export const CafeOwnerAuthProvider: React.FC<CafeOwnerAuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cafeOwner, setCafeOwner] = useState<CafeOwner | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('cafeOwnerToken');
      const cachedOwner = localStorage.getItem('cafeOwnerData');

      if (token) {
        // If we have cached owner data, use it immediately and verify in background
        if (cachedOwner) {
          try {
            const ownerData = JSON.parse(cachedOwner);
            setIsAuthenticated(true);
            setCafeOwner(ownerData);
            setLoading(false);

            // Verify token in background
            verifyTokenInBackground(token);
            return;
          } catch (error) {
            localStorage.removeItem('cafeOwnerData');
          }
        }

        // No cached data, verify token
        await verifyToken(token);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Cafe owner auth check failed:', error);
      localStorage.removeItem('cafeOwnerToken');
      localStorage.removeItem('cafeOwnerData');
      setLoading(false);
    }
  };

  const verifyToken = async (token: string) => {
    try {
      const response = await axios.get('https://api.sobcof.com/api/cafe-owner/me', {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 5000
      });

      if (response.data.success) {
        setIsAuthenticated(true);
        setCafeOwner(response.data.data);
        localStorage.setItem('cafeOwnerData', JSON.stringify(response.data.data));
      } else {
        clearAuthData();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  const verifyTokenInBackground = async (token: string) => {
    try {
      const response = await axios.get('https://api.sobcof.com/api/cafe-owner/me', {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 5000
      });

      if (response.data.success) {
        localStorage.setItem('cafeOwnerData', JSON.stringify(response.data.data));
        setCafeOwner(response.data.data);
      } else {
        clearAuthData();
      }
    } catch (error) {
      console.error('Background token verification failed:', error);
      clearAuthData();
    }
  };

  const clearAuthData = () => {
    localStorage.removeItem('cafeOwnerToken');
    localStorage.removeItem('cafeOwnerData');
    setIsAuthenticated(false);
    setCafeOwner(null);
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      console.log('🔐 Cafe owner login attempt started');

      const response = await axios.post('https://api.sobcof.com/api/cafe-owner/login', {
        email,
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('✅ Cafe owner login response received:', response.data);

      if (response.data.success) {
        const { token, cafeOwner } = response.data.data;
        localStorage.setItem('cafeOwnerToken', token);
        localStorage.setItem('cafeOwnerData', JSON.stringify(cafeOwner));
        setIsAuthenticated(true);
        setCafeOwner(cafeOwner);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('❌ Cafe owner login failed:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('cafeOwnerToken');
      if (token) {
        await axios.post('https://api.sobcof.com/api/cafe-owner/logout', {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
    }
  };

  const value: CafeOwnerAuthContextType = {
    isAuthenticated,
    cafeOwner,
    login,
    logout,
    loading
  };

  return (
    <CafeOwnerAuthContext.Provider value={value}>
      {children}
    </CafeOwnerAuthContext.Provider>
  );
};
